import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import useUiuxStore from "src/store/useUiuxStore";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags  } from "src/utils/grid/gridStyleUtils";
import { formatDate, formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";

import { GridMonthCalendar } from "src/components/common/form/GridMonthCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultWMS2061_R01Dto, WMS2061_R01Dto } from "./Dto_WMS2061_R01";
import { useWMS2061_R01Store } from "./Store_WMS2061_R01";
import { formatYear } from "react-calendar/dist/shared/dateFormatter";
import { changeWarehouseColumn, CommonCodeColumn, getCommonCodeColumnOptions, getWarehouseColumnOptions, WarehouseColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */
 
interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<WMS2061_R01Dto> | null>;
}

const COLUMN_STATE_KEY = 'WMS2061_R01-grid-column-state';

export const WMS2061_R01Table = (props: Props) => {

	const { 
		clickable, setClickable,
		gridRef,
	} = props;

    const { 
		WMS2061_R01List, setWMS2061_R01List,
		insertWMS2061_R01List,
		removeWMS2061_R01List,
		updateWMS2061_R01List,
		deletedWMS2061_R01List, removeDeletedWMS2061_R01List, addDeletedWMS2061_R01List,
		selectedWMS2061_R01List, setSelectedWMS2061_R01List, removeSelectedWMS2061_R01List, addSelectedWMS2061_R01List, initSelectedWMS2061_R01List, allSelectedWMS2061_R01List,
		originalWMS2061_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useWMS2061_R01Store();
	const { compCdList, unitList, wmsCdList, warehouseCdList } = useCommonCodeStore();
    const { setCodeSearchModalProps } = useUiuxStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS2061_R01List,
		defaultGridData: defaultWMS2061_R01Dto,
		selectedRow: selectedWMS2061_R01List,
		originalGridData: originalWMS2061_R01List,
		updateRow: (index: number, data: any) => updateWMS2061_R01List(index, data),
	});

    const headerNames: Record<string, string> = {
		rowIndex:		"",
		logicCenter:	"물류센터",
		warehouseCd:	"창고코드",
		itemCd:			"품목코드",
		itemNm:			"품명",
		itemSpec:		"규격",
		locationNm:		"보관위치",
		itemNo:			"품목번호",
		lotNo:			"LOT No",
		lotQty:			"전산재고",
		updateLotQty:	"실재고",
		movedLotQty:	"이동량",
		remark:			"비고",
    }
	
	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['logicCenter', 'warehouseCd', 'itemCd', 'itemNm', 'itemSpec', 'locationNm', 'itemUnit', 'itemNo', 'lotNo', 'lotQty', 'movedLotQty'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		pointerCells: ['updateLotQty'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: ['warehouseCd'],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<WMS2061_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedWMS2061_R01List,
            deletedList: deletedWMS2061_R01List,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		createCellClassRules,
        selectedWMS2061_R01List, deletedWMS2061_R01List,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'updateLotQty' ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = [ 'updateLotQty' ];
	

	const baseColumnDefs: ColDef<WMS2061_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '물류센터', colId: 'logicCenter', field: 'logicCenter', width: 100,
			...CommonCodeColumn.options(wmsCdList, false, undefined, undefined),
		},
		{
			headerName: '창고코드', colId: 'warehouseCd', field: 'warehouseCd', width: 100,
			// 3번째 인자로 함수 전달: params.data에서 logicCenter 값을 가져옴
			...WarehouseColumn.options(warehouseCdList, false, (params: any) => params.data?.logicCenter, undefined, undefined),
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 140,
			editable: false,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 180,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			editable: false,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			editable: false,
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 80,
			editable: false,
		},
		{
			headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 140,
			editable: false,
		},
		{
			headerName: '전산재고', colId: 'lotQty', field: 'lotQty', width: 100,
			editable: false,
		},
		{
			headerName: '실재고', colId: 'updateLotQty', field: 'updateLotQty', width: 100,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '이동량', colId: 'movedLotQty', field: 'movedLotQty', width: 100,
			editable: false,
		},
		// ...IndexParameterCell(),
	], [deletedWMS2061_R01List, WMS2061_R01List, wmsCdList, warehouseCdList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<WMS2061_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<WMS2061_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState]);

    
	


    const handleCellChange = useCellChangeHandler({
        dataList: WMS2061_R01List,
        originalDataList: originalWMS2061_R01List,
        updateRow: updateWMS2061_R01List,
		customAfterUpdate: (updatedData, field, value) => {
			if(field === 'logicCenter') {
				updatedData.warehouseCd = '';
			}
			return updatedData;
		},
    });

    const handleRowClick = useRowSelectionHandler({
        dataList: WMS2061_R01List,
        idKey: 'valueIndex',
        selectedList: selectedWMS2061_R01List,
        deletedList: deletedWMS2061_R01List,
        addSelected: addSelectedWMS2061_R01List,
        removeSelected: removeSelectedWMS2061_R01List,
        clickable, setClickable
    });

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: WMS2061_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<WMS2061_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(WMS2061_R01List, 'valueIndex', cellClickActions);
	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<WMS2061_R01Dto>[] = useMemo(() => [
	], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(WMS2061_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<WMS2061_R01Dto>) => {
		postSortHandler(params, WMS2061_R01List, originalWMS2061_R01List, setWMS2061_R01List);
	}, [WMS2061_R01List, originalWMS2061_R01List, ]);

	const pinnedBottomRow = useMemo(() => {
		return [{
			rowIndex: '계',
			itemCd: WMS2061_R01List.length.toLocaleString() + "건",
			editable: false,
		}];
	},[WMS2061_R01List.length]);

    return (
		<GridContextWrapper<WMS2061_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setWMS2061_R01List}
			initSelected={initSelectedWMS2061_R01List}
			defaultRowData={defaultWMS2061_R01Dto}
			allSelected={allSelectedWMS2061_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<WMS2061_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<WMS2061_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedWMS2061_R01List,
					deletedList: deletedWMS2061_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				
			/>
		</GridContextWrapper>
    )
}
